import { useEffect, useRef, useState } from 'react';
import { ArrowRight, CircleCheck, CircleX, Crop, ImagePlus, Lightbulb, Loader2, Lock, Plus, RefreshCw } from 'lucide-react';
import cropExampleTooWide from '../../../../assets/images/crop-example-too-wide.jpg';
import cropExampleJustRight from '../../../../assets/images/crop-example-just-right.jpg';
import Button from '../../../ui/Button';
import WizardShell from '../../../ui/WizardShell';
import BottomBar from '../../../ui/BottomBar';
import BookSidePanel from '../../../ui/BookSidePanel';
import PageCropScreen from '../../../builder/PageCropScreen';
import { useBuilderBack, useBuilderBusy, useBuilderStepInterceptor } from '../../../builder/BuilderNavContext';
import { BASE_BOOK_PRICE } from '../../../../config/pricing';
import { isEtsyUser } from '../../../../services/etsyService';
import { useSelector } from 'react-redux';
import {
  deleteCoverImage,
  setBatchId,
  setCoverImages,
  setIsReusingBatch,
} from '../../../../redux/slice/sketchSlice';
import { useDispatch } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { saveStep } from '../../../../services/stepService';
import { removeBookImage } from '../../../../services/bookService';
import FileUploader from '../../../common/UppyUploader';
import { BUILDER_ACTIONS, BUILDER_STEPS, countBuilderAction, useBuilderStepVisit } from '../../../../util/builderStepTiming';
import { markStepAsVisited } from '../../../../redux/slice/profileSlice';
import { run, warmup } from '../../../../services/runpodService';
import { autoCropImageWithRect, cropCacheKey, CROP_SAVE_FAILED_MESSAGE, isSmartCropEnabled, processAllImagesForRun, uploadUserImage } from '../../../../util/imageCropUploadUtils';
import { warmFaceDetector } from '../../../../util/faceDetector';
import { buildCropUploadRef } from '../../../../services/stagingUploadService';
import {
  fetchBatchImagesByBookId,
  isStagingUploadEnabled,
} from '../../../../services/mediaService';
import { savePendingCoverImage, loadPendingCoverImage, clearPendingCoverImage, markCoverAsSentToRun, clearCoverSentToRun, clearPendingImages, carryPendingCoverCropData } from '../../../../util/pendingImageStorage';
import { getMediaMongoId, isMongoMediaId } from '../../../../util/placeOrderImageUtils';
import { mapDbCoverToGridImage } from '../../../../util/stagingBookImageUtils';
import WorthALookNotice from './WorthALookNotice';
import { setShouldWarmupCover } from '../../../../redux/slice/stepSlice';
import { notifySaved } from '../../../../util/saveTracker';
import CoverPreview from '../../../builder/CoverPreview';

function UploadCoverPhoto() {
  const [croppingImage, setCroppingImage] = useState(null);
  // The picked cover is on its way: uploading (the uploader's count), then auto-cropping.
  const [uploaderBusy, setUploaderBusy] = useState(0);
  const [placingPicked, setPlacingPicked] = useState(false);
  const [deletedFilenames, setDeletedFilenames] = useState([]);
  const [blobUrlCache, setBlobUrlCache] = useState({}); // Cache blob URLs
  const uploaderRef = useRef(null);
  // Set while "Replace" waits for the picker: the next upload takes the old cover's place.
  const replacingRef = useRef(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tag = searchParams.get("tag");
  const coverImages = useSelector((state) => state.sketch.coverImages);
  const { activeBookId, uploadingCoverImage, shouldWarmupCover } = useSelector(
    (state) => state.stepFlow
  );
  useBuilderStepVisit(BUILDER_STEPS.COVER, activeBookId);
  const { session } = useSelector((state) => state.authProvider);
  const { myBooks, data: profileData } = useSelector((state) => state.profile);

  useEffect(() => {
    dispatch(setBatchId(uuidv4()));
    dispatch(setIsReusingBatch(true));
  }, [dispatch]);

  // The face detector is usually warm from the title step; make sure.
  useEffect(() => {
    isSmartCropEnabled().then((on) => { if (on) warmFaceDetector(); });
  }, []);

  // Restore cover from DB (staging) or localStorage on mount
  useEffect(() => {
    if (!activeBookId || coverImages.length > 0) return;

    let cancelled = false;

    (async () => {
      try {
        if (await isStagingUploadEnabled()) {
          const batch = await fetchBatchImagesByBookId(activeBookId);
          const coverRow = batch?.docs?.data?.find((image) => image.isCover);
          const mapped = mapDbCoverToGridImage(coverRow);
          if (!cancelled && mapped) {
            dispatch(setCoverImages([carryPendingCoverCropData(mapped, activeBookId)]));
            return;
          }
        }
      } catch (err) {
        console.warn('Failed to load cover from API:', err);
      }

      if (cancelled) return;
      const pending = loadPendingCoverImage(activeBookId);
      if (pending) {
        dispatch(setCoverImages([pending]));
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBookId]);

  // Cleanup blob URLs on unmount ONLY
  useEffect(() => {
    return () => {
      Object.values(blobUrlCache).forEach(blobUrl => {
        if (blobUrl && blobUrl.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrl);
        }
      });
    };
  }, [blobUrlCache]);

  /* useEffect(() => {
    if (shouldWarmupCover) {
      warmup({ workflowType: 'cover' });
      dispatch(setShouldWarmupCover(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldWarmupCover]) */

  // Function to get or create blob URL for an image
  const getBlobUrlForImage = async (image) => {
    const imageId = cropCacheKey(image);

    const cachedBlobUrl = blobUrlCache[imageId];

    //  Validate cached blob URL
    if (cachedBlobUrl) {
      try {
        await fetch(cachedBlobUrl);
        return cachedBlobUrl;
      } catch {
        //  Dead blob → remove from cache
        setBlobUrlCache(prev => {
          const next = { ...prev };
          delete next[imageId];
          return next;
        });
      }
    }

    // The crop tool edits the ORIGINAL photo (the rectangle is measured on
    // it); `file` is the auto-cropped or hand-cropped copy once one exists.
    if (image.originalFile instanceof Blob) {
      const blobUrl = URL.createObjectURL(image.originalFile);
      setBlobUrlCache(prev => ({ ...prev, [imageId]: blobUrl }));
      return blobUrl;
    }
    if (image.originalUrl && !image.originalUrl.startsWith('blob:')) {
      try {
        const response = await fetch(image.originalUrl);
        if (response.ok) {
          const blobUrl = URL.createObjectURL(await response.blob());
          setBlobUrlCache(prev => ({ ...prev, [imageId]: blobUrl }));
          return blobUrl;
        }
      } catch (err) {
        console.warn('Failed to fetch original cover for cropping; using current copy:', err);
      }
    }

    //  If File/Blob exists, use it
    if (image.file instanceof Blob || image.file instanceof File) {
      const blobUrl = URL.createObjectURL(image.file);
      setBlobUrlCache(prev => ({ ...prev, [imageId]: blobUrl }));
      return blobUrl;
    }

    //  Fetch from server URL
    const sourceUrl =
      image.url ||
      image.AIinfo?.outputUrls?.[0]?.original_presigned_url;

    if (!sourceUrl) {
      toast.error('No valid image source found');
      return null;
    }

    const response = await fetch(sourceUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    setBlobUrlCache(prev => ({ ...prev, [imageId]: blobUrl }));
    return blobUrl;
  };

  const handleCropSave = async ({ blob, url, cropData }) => {
    // Validate required parameters
    if (!blob || !url || !cropData) {
      console.error('Invalid crop data received:', {
        blob: !!blob,
        url: !!url,
        cropData: !!cropData,
      });
      toast.error(CROP_SAVE_FAILED_MESSAGE);
      return;
    }

    if (!croppingImage) {
      console.error('No cropping image reference found');
      return;
    }
    countBuilderAction(BUILDER_ACTIONS.CROP_SAVED);
    if (croppingImage.cropReview?.flagged) countBuilderAction(BUILDER_ACTIONS.CROP_FLAGGED_SAVED);

    // Set loading state immediately so the grid shows the animation while Cloudinary upload is in progress
    const loadingImages = coverImages.map((img) => {
      const cond = !!img._id
        ? img._id === croppingImage._id
        : img.id === croppingImage.id;
      return cond ? { ...img, isLoadingCover: true } : img;
    });
    dispatch(setCoverImages(loadingImages));

    try {
      // Determine the filename to use with fallback chain
      const getFilename = (img) => {
        if (img.originalFilename) return img.originalFilename;
        if (img.file?.name) return img.file.name;
        if (img.filename) return img.filename;
        return `cropped-image-${Date.now()}.jpg`;
      };

      // Validate blob before creating File
      if (!(blob instanceof Blob)) {
        throw new Error('Invalid blob object received');
      }

      // Upload cropped blob to Cloudinary so the crop persists across refresh
      const cropFilename = getFilename(croppingImage);
      const cropFile = new File([blob], cropFilename, {
        type: blob.type || 'image/jpeg',
        lastModified: Date.now(),
      });

      let croppedCloudinaryUrl = null;
      let croppedPublicId = null;
      let croppedStagingKey = null;
      try {
        const mediaId = getMediaMongoId(croppingImage);
        const uploadResult = await uploadUserImage(cropFile, cropFilename, {
          bookId: activeBookId,
          variant: 'cropped',
          isCover: true,
          mediaId,
          cropData,
          cropReview: null,
        });
        croppedCloudinaryUrl = buildCropUploadRef(uploadResult);
        croppedPublicId = uploadResult.publicId;
        croppedStagingKey = uploadResult.stagingKey || null;
      } catch (uploadErr) {
        console.warn('Failed to upload cover crop (crop will not persist on refresh):', uploadErr);
        toast.error(CROP_SAVE_FAILED_MESSAGE);
        dispatch(setCoverImages(coverImages));
        return;
      }

      const updated = coverImages.map((img) => {
        const cond = !!img._id
          ? img._id === croppingImage._id
          : img.id === croppingImage.id;

        if (cond) {
          const filename = getFilename(img);

          try {
            const newFile = new File([blob], filename, {
              type: blob.type || 'image/jpeg',
              lastModified: Date.now(),
            });

            return {
              ...img,
              url,
              file: newFile,
              originalFile: img.originalFile || img.file, // Preserve original
              cropData,
              // A hand-made crop needs no second look.
              cropReview: null,
              originalUrl: img.originalUrl || img.url,
              originalFilename: img.originalFilename || img.filename || filename,
              lastModified: Date.now(),
              isCropped: true,
              AIinfo: croppedPublicId ? { public_id: croppedPublicId } : null,
              AIUrl: null,
              publicId: croppedPublicId || img.publicId,
              croppedCloudinaryUrl,
              croppedStagingKey,
            };
          } catch (fileError) {
            console.error('Error creating File object:', fileError);
            throw new Error(`Failed to create file: ${fileError.message}`);
          }
        }
        return img;
      });

      if (!updated || updated.length !== coverImages.length) {
        throw new Error('Failed to update images array properly');
      }

      dispatch(setCoverImages(updated));
      if (updated[0]) {
        clearCoverSentToRun(activeBookId);
        savePendingCoverImage(activeBookId, { ...updated[0], sentToRun: false });
      }
      setCroppingImage(null);
      notifySaved();

    } catch (error) {
      console.error('Error in handleCropSave:', error);
      // The technical reason stays in the console; the customer gets plain words.
      toast.error(CROP_SAVE_FAILED_MESSAGE);
    }
  };

  /**
   * Upload an auto-cropped cover so the crop persists across reloads, then
   * record the staging key on the image. Failure leaves the in-memory crop in
   * place; generation uploads the blob itself (processAllImagesForRun).
   */
  const uploadCoverCropInBackground = async (img) => {
    const sameImage = (other) =>
      (img._id && other._id && String(other._id) === String(img._id)) || other.id === img.id;
    try {
      const uploadResult = await uploadUserImage(img.file, img.filename || 'cover.jpg', {
        bookId: activeBookId,
        variant: 'cropped',
        isCover: true,
        mediaId: getMediaMongoId(img),
        cropData: img.cropData,
        cropReview: img.cropReview,
      });
      const croppedCloudinaryUrl = buildCropUploadRef(uploadResult);
      const croppedStagingKey = uploadResult.stagingKey || null;
      dispatch((dispatch, getState) => {
        const current = getState().sketch?.coverImages || [];
        if (!current.some(sameImage)) return;
        const updated = current.map((other) =>
          sameImage(other)
            ? {
                ...other,
                croppedCloudinaryUrl,
                croppedStagingKey,
                publicId: uploadResult.publicId || other.publicId,
                isLoadingCover: false,
              }
            : other,
        );
        dispatch(setCoverImages(updated));
        if (updated[0]) savePendingCoverImage(activeBookId, updated[0]);
      });
    } catch (err) {
      console.warn('Cover auto-crop upload failed (crop will not persist on refresh):', err);
      dispatch((dispatch, getState) => {
        const current = getState().sketch?.coverImages || [];
        dispatch(setCoverImages(current.map((other) => (sameImage(other) ? { ...other, isLoadingCover: false } : other))));
      });
    }
  };

  const handleImageUpload = async (files) => {
    countBuilderAction(BUILDER_ACTIONS.PHOTOS_ADDED, files?.length);
    if (activeBookId && (await isStagingUploadEnabled())) {
      clearPendingImages(activeBookId);
    }

    const newImages = files.map((file) => {
      const mongoId =
        file.mediaId && isMongoMediaId(String(file.mediaId))
          ? String(file.mediaId)
          : null;

      return {
        id: mongoId || uuidv4(),
        ...(mongoId ? { _id: mongoId } : {}),
        url: file.url,
        publicId: file.publicId,
        stagingKey: file.stagingKey || file.publicId || null,
        file: file.file?.data || file.file,
        originalFile: file.file?.data || file.file,
        filename: file.filename,
        originalUrl: file.url,
        cropData: null,
      };
    });

    const existingFilenames = new Set(coverImages.map((img) => img.filename));
    const filtered = newImages.filter(
      (img) =>
        !existingFilenames.has(img.filename) &&
        !deletedFilenames.includes(img.filename)
    );

    // Same automatic page crop as the photos step: centred on the faces,
    // zoomed when they are small, centre crop otherwise. The cropped copy is
    // uploaded in the background so it survives a reload; generation can run
    // from the in-memory blob before that finishes.
    let noDetectorCount = 0;
    const autoCropped = await Promise.all(
      filtered.map(async (img) => {
        if (!(img.file instanceof Blob)) return img;
        const sourceUrl = URL.createObjectURL(img.file);
        try {
          const startedAt = Date.now();
          const { blob, cropData, method, faceCount, review } = await autoCropImageWithRect(sourceUrl);
          console.log(
            `Cover auto-crop (${method}, ${faceCount} faces) took ${Date.now() - startedAt}ms for`,
            img.filename,
            '| review',
            JSON.stringify(review),
          );
          if (method === 'center-no-detector') noDetectorCount += 1;
          const croppedFile = new File([blob], img.filename || 'cover.jpg', {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          return {
            ...img,
            url: URL.createObjectURL(blob),
            file: croppedFile,
            originalFile: img.originalFile || img.file,
            originalUrl: img.originalUrl || img.url,
            originalFilename: img.filename,
            cropData,
            cropReview: review,
            isCropped: true,
            isLoadingCover: true,
          };
        } catch (err) {
          console.warn('Cover auto-crop failed for', img.filename, '- using original:', err);
          return img;
        } finally {
          URL.revokeObjectURL(sourceUrl);
        }
      }),
    );

    const flaggedCount = autoCropped.filter((img) => img.cropReview?.flagged).length;
    if (flaggedCount > 0) countBuilderAction(BUILDER_ACTIONS.CROP_FLAGGED, flaggedCount);
    if (noDetectorCount > 0) countBuilderAction(BUILDER_ACTIONS.CROP_NO_DETECTOR, noDetectorCount);

    // The photo just picked goes first: the step shows coverImages[0] and sends it to drawing.
    // Read the list as it is now, not as it was when the picker opened.
    if (autoCropped.length > 0) {
      dispatch((dispatch, getState) => {
        const current = getState().sketch?.coverImages || [];
        // Same record (S3 reuses the cover record) or same entry: the new photo replaces it.
        const key = (img) => String(img._id ?? img.id ?? '');
        const isNew = (img) => autoCropped.some((n) => n.id === img.id || key(n) === key(img));
        dispatch(setCoverImages([...autoCropped, ...current.filter((img) => !isNew(img))]));
      });
    }

    // Persist cover image to localStorage (only the first/active one)
    // The new photo, not whatever was there before it (a Replace keeps the old cover in the list until it is deleted).
    if (autoCropped.length > 0) {
      savePendingCoverImage(activeBookId, autoCropped[0]);
    }

    autoCropped
      .filter((img) => img.isCropped && img.file instanceof Blob)
      .forEach((img) => uploadCoverCropInBackground(img));

    if (filtered.length > 0) {
      notifySaved();
    }
    return autoCropped;
  };

  const handleSketchGeneration = async () => {
    // Validation only
    if (!activeBookId) {
      return;
    }

    if (!coverImages || coverImages.length === 0) {
      toast.error('Please upload at least one image to continue.');
      return;
    }

    // Check if already generated
    if (!!coverImages[0]?.AIinfo?.aws_upload?.s3_url || !!coverImages[0]?.AIUrl) {
      // Navigate immediately
      dispatch(
        markStepAsVisited({ 
          id: activeBookId, 
          currentStepCode: 'Upload_Your_Cover_Photo' 
        })
      );
      navigate(`/create?stepCode=${'Upload_Images'}${tag ? `&tag=${tag}` : ""}`);

      // Save step progress in the background
      saveStep({
        userId: session?.user?._id,
        bookId: activeBookId,
        isCompleted: 'yes',
        stepCode: 'Upload_Images',
      }).catch(err => console.error("Error saving step in background:", err));
      return;
    }

    //  Fire the async process WITHOUT await
    processAndStartJob();

    //  Navigate immediately
    dispatch(
      markStepAsVisited({
        id: activeBookId,
        currentStepCode: 'Upload_Images',
      })
    );
    navigate(`/create?stepCode=${'Upload_Images'}${tag ? `&tag=${tag}` : ""}`);

    //  Save step progress in the background
    saveStep({
      userId: session?.user?._id,
      bookId: activeBookId,
      isCompleted: 'yes',
      stepCode: 'Upload_Images',
    }).catch(err => console.error("Error saving step in background:", err));
  };

  // Separate function that runs in background
  const processAndStartJob = async () => {
    try {
      console.log(' Starting cover generation process...');
      
      // Only the cover on screen. The cover job saves its first output as the
      // cover and any further ones as interior pages, so a second entry in the
      // list (an old cover kept during a Replace) showed up in the page grid.
      const processedImages = await processAllImagesForRun(coverImages.slice(0, 1), activeBookId, {
        isCover: true,
      });
      
      if (!processedImages || processedImages.length === 0) {
        throw new Error('Failed to process images');
      }

      console.log(' All images processed and ready for /run:', processedImages);

      const response = await run({
        imageUrls: processedImages,
        title: 'Cover Image',
        bookId: activeBookId
      });

      if (!response?.success) {
        throw new Error(response?.message || 'Failed to start job');
      }

      // Mark as sent to /run in persistent storage
      markCoverAsSentToRun(activeBookId);

      // Cover is now being processed — do NOT clear yet; wait for API/Socket to confirm
      // clearPendingCoverImage(activeBookId);

      console.log(' Job started:', response.data);

    } catch (err) {
      console.error('Error in background processing:', err);
      toast.error(
        err.message || 'Failed to start image generation. Please try again.'
      );
    }
  };

  const handleDeleteImage = async (id) => {
    if (uploadingCoverImage) {
      toast.warning(
        "Cannot delete image while it's being processed. Please wait for generation to complete."
      );
      return;
    }

    const toDelete = coverImages.find((img) => img.id === id || img._id === id);
    if (toDelete) {
      countBuilderAction(BUILDER_ACTIONS.PHOTOS_REMOVED);
      //  Only revoke if we created it (blob URLs start with 'blob:')
      const imageId = cropCacheKey(toDelete);
      if (blobUrlCache[imageId] && blobUrlCache[imageId].startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlCache[imageId]);
        setBlobUrlCache(prev => {
          const updated = { ...prev };
          delete updated[imageId];
          return updated;
        });
      }

      setDeletedFilenames((prev) => [...prev, toDelete.filename]);
      dispatch(deleteCoverImage(id));
      clearPendingCoverImage(activeBookId);
      removeBookImage(id);
    }
  };

  // Handle opening crop modal with blob URL
  const handleEditImage = async (image) => {
    if (uploadingCoverImage) {
      toast.warning(
        "Cannot edit image while it's being processed. Please wait for generation to complete."
      );
      return;
    }
    countBuilderAction(BUILDER_ACTIONS.CROP_OPENED);
    if (image?.cropReview?.flagged) countBuilderAction(BUILDER_ACTIONS.CROP_FLAGGED_OPENED);

    // Show loading state while fetching blob
    // const loadingToast = toast.info('Loading image...', { autoClose: false });
    
    const blobUrl = await getBlobUrlForImage(image);
    
    // if (loadingToast) {
    //   toast.dismiss(loadingToast);
    // }

    if (blobUrl) {
      setCroppingImage({
        ...image,
        blobUrl, // Pass blob URL for cropper
      });
    }
  };

  // ---- step 2 on the redesign (docs/design/HANDOFF.md: Cover, CoverLocked and their tablet and desktop versions)

  const cover = coverImages[0] || null;
  const coverReady = !!(cover?.AIinfo?.aws_upload?.s3_url || cover?.AIUrl);
  const pendingCover = activeBookId ? loadPendingCoverImage(activeBookId) : null;
  const locked = !!cover && !coverReady && (uploadingCoverImage || !!pendingCover?.sentToRun);
  const photoUrl = cover?.blobUrl || cover?.url || null;
  // The photo is still uploading, or its crop is still being saved: nothing that changes it or leaves
  // the step works until it's stored (leaving would cancel the upload).
  const busy = !locked && (uploaderBusy > 0 || placingPicked || !!cover?.isLoadingCover);
  // The picked photo is uploading or being auto-cropped (CoverAdding).
  const adding = !locked && (uploaderBusy > 0 || placingPicked);
  useBuilderBusy(busy);
  const activeBook = (myBooks || []).find((b) => b._id === activeBookId);
  const showPrice = !isEtsyUser(profileData) && !activeBook?.gift?.giftOrderId;

  const openPicker = () => {
    if (!uploaderRef.current?.isReady?.()) {
      toast.info('One moment, the uploader is still getting ready.');
      return;
    }
    uploaderRef.current.openPicker();
  };
  const replaceCover = () => {
    if (locked || busy) return;
    replacingRef.current = !!cover;
    openPicker();
  };
  const onPicked = async (files) => {
    const old = cover;
    const added = await handleImageUpload(files);
    const oldId = String(old?._id ?? old?.id ?? '');
    // The S3 upload keeps one cover record per book and puts the new photo in it,
    // so the new photo can come back with the old cover's id. Deleting "the old
    // cover" then deleted the photo just picked (empty frame, original lost).
    const reusedRecord = (added || []).some((img) => String(img._id ?? img.id ?? '') === oldId);
    const replacing = replacingRef.current;
    replacingRef.current = false;
    if (replacing && old && !reusedRecord) {
      await handleDeleteImage(old.id ?? old._id);
      // The replaced photo may be picked again later; it was never "deleted" by the customer.
      if (old.filename) setDeletedFilenames((prev) => prev.filter((f) => f !== old.filename));
    }
  };

  const goBackToTitle = () => {
    saveStep({ userId: session?.user?._id, bookId: activeBookId, isCompleted: 'yes', stepCode: 'Create_New_Book' });
    dispatch(markStepAsVisited({ id: activeBookId, currentStepCode: 'Create_New_Book' }));
    navigate(`/create-book?stepCode=Create_New_Book${tag ? `&tag=${tag}` : ''}`);
  };
  useBuilderBack(goBackToTitle);

  // A forward click in the header's stepper starts the cover drawing first, as Next does.
  useBuilderStepInterceptor(({ stepNumber: target, navigate: performNavigate }) => {
    if (target > 2 && cover && !coverReady && !pendingCover?.sentToRun) {
      processAndStartJob();
    }
    performNavigate();
  });

  // CoverEmpty (docs/design/HANDOFF.md): with no photo the main button opens the picker; it is never a disabled Next.
  // TODO(design): the busy Next isn't drawn; it follows Page photos' "Preparing your photos" button.
  const primary = busy ? (
    <Button size='lg' fullWidth busy>
      Adding your photo
    </Button>
  ) : cover ? (
    <Button size='lg' fullWidth onClick={handleSketchGeneration}>
      Next: Add page photos
    </Button>
  ) : (
    <Button size='lg' fullWidth onClick={openPicker} icon={<Plus className='h-5 w-5' aria-hidden='true' />}>
      Choose a cover photo
    </Button>
  );
  const sidePanel = (
    <BookSidePanel
      // The cover as it prints; until it's drawn, the photo stands in for the drawing.
      cover={
        <CoverPreview
          title={activeBook?.title}
          imageUrl={locked ? null : photoUrl}
          placeholder={
            locked ? (
              <div className='flex h-full flex-col items-center justify-center gap-2 bg-[#E7DCCF] text-md-pink-dark'>
                <Loader2 className='h-6 w-6 animate-spin' aria-hidden='true' />
                <span className='text-[13px] font-semibold'>Drawing your cover</span>
              </div>
            ) : (
              <div className='flex h-full flex-col items-center justify-center gap-1.5 text-[#8A7F78]'>
                <ImagePlus className='h-7 w-7' aria-hidden='true' />
                <span className='text-[13px] text-md-muted'>Cover photo next</span>
              </div>
            )
          }
        />
      }
      title={activeBook?.title}
      rows={showPrice ? [{ label: 'Cover + 20 pages', value: `$${BASE_BOOK_PRICE.toFixed(2)}` }] : []}
      primary={primary}
      back={{ onClick: goBackToTitle, disabled: busy }}
    />
  );

  const lockNote = (
    <div role='note' className='flex items-start gap-2.5 rounded-xl border border-md-pink-light-1 bg-white px-3.5 py-3 text-sm leading-snug text-[#4A4446]'>
      <Lock className='mt-0.5 h-4 w-4 flex-shrink-0 text-md-pink-dark' aria-hidden='true' />
      <span>This photo is locked while we draw your cover. Keep going and add your page photos. You can change the cover photo once it's done.</span>
    </div>
  );

  // The "Crop in close" panel's two example photos; the too-wide one shows where the frame should have gone.
  // They fill the space left under the text (the panel is as tall as the photo beside it) and sit centred in it:
  // each is as tall as both the free height and half the width allow (container units on the row), up to the
  // mockup's size from xl. Without container-unit support they fall back to a fixed 112px. The overlay crops
  // like the photo (slice = cover).
  const exampleBox = 'relative aspect-[124/165] h-[112px] overflow-hidden rounded-xl xl:!max-h-[133px]';
  const exampleSize = { height: 'min(calc(100cqh - 28px), calc((100cqw - 40px) / 2 * 165 / 124), 180px)' };
  const cropExamples = (
    <div className='flex min-h-0 flex-1 items-center justify-center gap-2 [container-type:size]'>
      <div className='flex min-w-0 flex-col items-center gap-2'>
        <div aria-hidden='true' className={`${exampleBox} ring-1 ring-[#DCCFC0]`} style={exampleSize}>
          <img src={cropExampleTooWide} alt='' className='absolute inset-0 h-full w-full object-cover' />
          <svg viewBox='0 0 124 165' preserveAspectRatio='xMidYMid slice' focusable='false' className='absolute inset-0 h-full w-full'>
            <path d='M0 0H124V165H0Z M44 65.9H85.5V121.3H44Z' fill='rgba(27,13,18,0.4)' fillRule='evenodd' />
            <rect x='44' y='65.9' width='41.5' height='55.4' fill='none' stroke='#FFFFFF' strokeWidth='0.8' />
            <path
              d='M44 71.9V65.9H50M79.5 65.9H85.5V71.9M85.5 115.3V121.3H79.5M50 121.3H44V115.3'
              fill='none'
              stroke='#FFFFFF'
              strokeWidth='2.2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
        <span className='flex h-5 items-center gap-[5px] whitespace-nowrap text-sm font-semibold text-md-muted lg:text-[13px] xl:text-sm'>
          <CircleX className='h-4 w-4 flex-shrink-0' aria-hidden='true' />
          Too wide
        </span>
      </div>
      <span aria-hidden='true' className='mb-7 flex flex-shrink-0 text-md-pink-dark'>
        <ArrowRight className='h-5 w-5' strokeWidth={2.2} />
      </span>
      <div className='flex min-w-0 flex-col items-center gap-2'>
        <div aria-hidden='true' className={`${exampleBox} ring-2 ring-md-pink-dark`} style={exampleSize}>
          <img src={cropExampleJustRight} alt='' className='absolute inset-0 h-full w-full object-cover' />
        </div>
        <span className='flex h-5 items-center gap-[5px] whitespace-nowrap text-sm font-bold text-md-pink-dark lg:text-[13px] xl:text-sm'>
          <CircleCheck className='h-4 w-4 flex-shrink-0' aria-hidden='true' />
          Just right
        </span>
      </div>
    </div>
  );

  return (
    <>
      <WizardShell aside={sidePanel} bottomBar={<BottomBar primary={primary} back={{ onClick: goBackToTitle, disabled: busy }} />}>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-1.5'>
            <h1 className='m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]'>
              {locked ? 'Your cover photo' : cover ? 'Your cover photo' : 'Choose a cover photo'}
            </h1>
            <p className='m-0 text-[15px] leading-snug text-md-muted md:text-[17px]'>
              {locked ? "We're turning this into a full-color illustration for the front of your book." : 'We turn this into a full-color illustration for the front of the book.'}
            </p>
          </div>

          <WorthALookNotice count={cover?.cropReview?.flagged && !locked ? 1 : 0} isCover />

          {/* The "Crop in close" panel sits beside the photo from tablet up (decided 2026-10-23); below xl the photo is 260px wide so the panel and its examples have room. */}
          <div className='flex flex-col gap-4 md:flex-row md:items-stretch md:gap-6 xl:gap-8'>
            {/* The photo frame: a big picker when empty, the photo with Crop and Replace once chosen. */}
            {/* On a phone the frame is 225 x 300 (decided 2026-10-23), empty or not, so the tip under it fits on screen. */}
            <div
              className='relative mx-auto flex-shrink-0 overflow-hidden rounded-2xl bg-[#E7DCCF] md:mx-0 h-[300px] w-[225px] md:h-[347px] md:w-[260px] xl:h-[400px] xl:w-[300px]'
              // A photo dropped on the cover photo replaces it (below xl there's no separate drop box).
              onDragOver={photoUrl && !locked && !busy ? (e) => e.preventDefault() : undefined}
              onDrop={
                photoUrl && !locked && !busy
                  ? (e) => {
                      e.preventDefault();
                      if (e.dataTransfer?.files?.length) {
                        replacingRef.current = true;
                        uploaderRef.current?.addFiles([e.dataTransfer.files[0]]);
                      }
                    }
                  : undefined
              }
            >
              {photoUrl ? (
                // Tapping the photo opens the crop screen, as the "Worth a look" note asks ("Tap it to double check").
                <button
                  type='button'
                  onClick={() => handleEditImage(cover)}
                  disabled={locked || busy}
                  aria-label='Adjust your cover photo'
                  className='block h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-md-pink'
                >
                  <img src={photoUrl} alt='Your cover' className='h-full w-full object-cover' />
                </button>
              ) : adding ? null : (
                // Hidden while the first photo is added: its text showed through the overlay.
                <button
                  type='button'
                  onClick={openPicker}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (e.dataTransfer?.files?.length) uploaderRef.current?.addFiles([e.dataTransfer.files[0]]);
                  }}
                  className='absolute inset-0 flex flex-col items-center justify-center gap-2 text-[#8A7F78] focus:outline-none focus-visible:ring-2 focus-visible:ring-md-pink'
                >
                  <ImagePlus className='h-9 w-9' aria-hidden='true' />
                  <span className='text-base font-bold text-md-dark'>Add a cover photo</span>
                  <span className='px-6 text-center text-sm text-md-muted'>
                    <span className='lg:hidden'>Tap to pick one from your camera roll.</span>
                    <span className='hidden lg:inline'>Drag a photo here or click to browse.</span>
                  </span>
                </button>
              )}
              {adding ? (
                // CoverAdding: the "Drawing your cover" overlay with its own words, over a plain frame or the old photo.
                <div role='status' className='absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-[rgba(27,13,18,0.45)] text-white'>
                  <Loader2 className='h-7 w-7 animate-spin' aria-hidden='true' />
                  <span className='text-[17px] font-bold'>Adding your photo</span>
                </div>
              ) : null}
              {locked ? (
                <div role='status' className='absolute inset-0 flex flex-col items-center justify-center gap-2.5 bg-[rgba(27,13,18,0.45)] pb-16 text-white'>
                  <Loader2 className='h-7 w-7 animate-spin' aria-hidden='true' />
                  <span className='text-[17px] font-bold'>Drawing your cover</span>
                </div>
              ) : null}
              {photoUrl || adding ? (
                // The phone frame is 225 wide: tighter spacing so both buttons keep their icon.
                // While adding they show disabled (busy), as CoverAdding draws them.
                <div className='absolute inset-x-2 bottom-2 grid grid-cols-2 gap-1.5 md:inset-x-3 md:bottom-3 md:gap-2'>
                  <Button
                    size='sm'
                    variant='secondary'
                    className='!gap-1.5 !border-0 !bg-white/95 !px-2.5 disabled:!bg-white/70 disabled:!text-[#8A8284] md:!gap-2 md:!px-4'
                    onClick={() => handleEditImage(cover)}
                    disabled={locked || busy}
                    icon={<Crop className='h-4 w-4 flex-shrink-0' aria-hidden='true' />}
                  >
                    Crop
                  </Button>
                  <Button
                    size='sm'
                    variant='secondary'
                    className='!gap-1.5 !border-0 !bg-white/95 !px-2.5 disabled:!bg-white/70 disabled:!text-[#8A8284] md:!gap-2 md:!px-4'
                    onClick={replaceCover}
                    disabled={locked || busy}
                    icon={<RefreshCw className='h-4 w-4 flex-shrink-0' aria-hidden='true' />}
                  >
                    Replace
                  </Button>
                </div>
              ) : null}
            </div>

            {/* From tablet up this column is exactly as tall as the photo frame beside it (decided 2026-10-23). */}
            <div className='flex min-w-0 flex-1 flex-col gap-3 md:h-[347px] md:gap-4 xl:h-[400px]'>
              {/* Mobile: one line (the lock note replaces it while the cover draws). Tablet and desktop: the "Crop in close" panel beside the photo. */}
              <div className={`flex items-start gap-3 rounded-xl border border-md-pink-light-1 bg-white px-3.5 py-3 md:hidden ${locked ? '!hidden' : ''}`}>
                <Lightbulb className='mt-px h-5 w-5 flex-shrink-0 text-md-pink-dark' aria-hidden='true' />
                <p className='m-0 text-sm leading-snug text-[#4A4446]'>Best results: crop in close around the people you want on your cover photo.</p>
              </div>
              <section className={`${locked ? 'md:!hidden xl:!flex' : ''} hidden min-h-0 rounded-2xl border border-md-pink-light-1 bg-md-pink-light-2 p-[18px] md:flex md:flex-1 md:flex-col md:gap-4 lg:p-3.5 xl:p-[18px]`}>
                <div className='flex min-w-0 flex-col gap-2'>
                  <div className='flex items-center gap-2.5'>
                    <span aria-hidden='true' className='flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-full bg-md-pink/10 text-md-pink-dark md:h-7 md:w-7 lg:hidden xl:flex'>
                      <Crop className='h-4 w-4' strokeWidth={2.2} />
                    </span>
                    <span className='whitespace-nowrap text-[13px] font-bold uppercase tracking-[0.06em] text-md-pink-dark'>For the best result</span>
                  </div>
                  <h2 className='m-0 font-heading text-[32px] font-normal leading-[1.1] text-md-dark md:text-[28px]'>Crop in close</h2>
                  <p className='m-0 text-base leading-snug text-md-muted md:text-[15px]'>Fill the frame with the people you want on your cover photo.</p>
                </div>
                {cropExamples}
              </section>
              {/* While the cover draws, the lock note sits under the panel from xl (DeskCoverLocked: the panel's examples
                  shrink to make room). Below xl both don't fit beside the photo, so the note takes the panel's place. */}
              {locked ? <div className='flex-shrink-0'>{lockNote}</div> : null}
              {!locked && cover ? (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (busy) return;
                    if (e.dataTransfer?.files?.length) {
                      replacingRef.current = !!cover;
                      uploaderRef.current?.addFiles([e.dataTransfer.files[0]]);
                    }
                  }}
                  className={`hidden flex-shrink-0 rounded-xl border-2 border-dashed border-md-control p-4 text-center text-[15px] text-md-muted xl:block ${busy ? 'opacity-60' : ''}`}
                >
                  Drop a {cover ? 'different ' : ''}photo here or{' '}
                  <button type='button' onClick={replaceCover} disabled={busy} className='font-semibold text-md-pink-dark underline underline-offset-2'>
                    browse files
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </WizardShell>

      <FileUploader
        ref={uploaderRef}
        variant='picker'
        onChange={async (files) => {
          setPlacingPicked(true);
          try {
            await onPicked(files);
          } finally {
            setPlacingPicked(false);
          }
        }}
        onBusy={setUploaderBusy}
        single
        bookId={activeBookId}
        isCover
      />

      <PageCropScreen
        open={!!croppingImage}
        image={croppingImage}
        imageUrl={croppingImage?.blobUrl}
        cropData={croppingImage?.cropData}
        title='Adjust your cover photo'
        tip='Best results: crop in close around the people you want on your cover.'
        subject='cover'
        flagged={!!croppingImage?.cropReview?.flagged}
        onSave={handleCropSave}
        onClose={() => setCroppingImage(null)}
        onReplace={() => {
          setCroppingImage(null);
          replaceCover();
        }}
      />
    </>
  );
}

export default UploadCoverPhoto;